import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import './ActualRideChart.css';

interface ActualRideChartProps {
  activityId: number;
  height?: number;
}

interface AnalyticsData {
  has_data: boolean;
  charts?: Record<string, string>;
  power_zones?: { zone: string; minutes: number; pct: number }[];
  hr_zones?: { zone: string; minutes: number; pct: number }[];
  best_efforts?: { duration: string; watts: number }[];
  normalized_power?: number;
  avg_power?: number;
  intensity_factor?: number;
  tss?: number;
  ftp?: number;
}

const ZONE_COLORS = ['#b0b0b0', '#4a90d9', '#5cb85c', '#f0ad4e', '#d9534f', '#1a1a1a', '#8b0000'];

export default function ActualRideChart({ activityId, height = 60 }: ActualRideChartProps) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/activities/${activityId}/analytics`, {
          credentials: 'include',
        });
        if (res.ok && !cancelled) {
          const json = await res.json();
          setData(json);
        }
      } catch {
        // silently fail
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [activityId]);

  if (loading) return <div className="actual-ride-loading">Loading ride data...</div>;
  if (!data || !data.has_data) return null;

  // Prefer power zones; fall back to HR zones for non-cycling activities
  const hasPower = (data.power_zones || []).some(z => z.minutes > 0);
  const zones = hasPower ? (data.power_zones || []) : (data.hr_zones || []);
  const totalMinutes = zones.reduce((sum, z) => sum + z.minutes, 0);
  const chartLabel = hasPower ? 'Power Zones' : (zones.length > 0 ? 'HR Zones' : '');

  return (
    <div className="actual-ride-chart">
      {chartLabel && <div className="actual-ride-label">{chartLabel}</div>}
      {zones.length > 0 && totalMinutes > 0 && (
        <div className="actual-ride-bars" style={{ height }}>
          {zones.map((z, i) => {
            if (z.minutes <= 0) return null;
            return (
              <div
                key={i}
                className="actual-ride-bar"
                style={{
                  flexGrow: z.minutes,
                  backgroundColor: ZONE_COLORS[i] || '#999',
                  height: `${20 + (i + 1) * 12}%`,
                }}
                title={`${z.zone}: ${z.minutes}min (${z.pct}%)`}
              />
            );
          })}
        </div>
      )}
      <div className="actual-ride-zone-legend">
        {zones.filter(z => z.minutes > 0).map((z, i) => {
          const origIdx = zones.indexOf(z);
          return (
            <span key={i} className="actual-ride-zone-item">
              <span className="actual-ride-zone-dot" style={{ backgroundColor: ZONE_COLORS[origIdx] }} />
              {z.zone.split(' ')[0]} {z.minutes}m
            </span>
          );
        })}
      </div>
      <div className="actual-ride-metrics">
        {data.avg_power != null && <span>Avg {Math.round(data.avg_power)}W</span>}
        {data.normalized_power != null && <span>NP {Math.round(data.normalized_power)}W</span>}
        {data.intensity_factor != null && <span>IF {data.intensity_factor}</span>}
        {data.tss != null && <span>TSS {Math.round(data.tss)}</span>}
      </div>
    </div>
  );
}
